import { ApiService } from '../ApiService';
import type { ApiResponse } from '../types';
import type { User } from '../../../types/auth';

export interface UserWithDetails extends User {
  department: string;
  lastLogin: string;
  status: 'active' | 'inactive' | 'pending';
  avatar?: string;
}

export interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'accountant' | 'viewer';
  department: string;
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  role?: 'admin' | 'accountant' | 'viewer';
  department?: string;
  status?: 'active' | 'inactive' | 'pending';
  avatar?: string;
}

export interface UsersListResponse {
  users: UserWithDetails[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface UserInviteRequest {
  email: string;
  role: 'admin' | 'accountant' | 'viewer';
  department: string;
  sendEmail?: boolean;
}

export const usersApi = {
  // Obtenir tous les utilisateurs
  getUsers: (params?: {
    page?: number;
    pageSize?: number;
    role?: string;
    status?: string;
    search?: string;
  }): Promise<ApiResponse<UsersListResponse>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params?.role) searchParams.append('role', params.role);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.search) searchParams.append('search', params.search);
    
    const query = searchParams.toString();
    return ApiService.get<UsersListResponse>(`/users${query ? `?${query}` : ''}`);
  },

  // Obtenir un utilisateur par ID
  getUserById: (userId: string): Promise<ApiResponse<UserWithDetails>> =>
    ApiService.get<UserWithDetails>(`/users/${userId}`),

  // Créer un nouvel utilisateur
  createUser: (userData: CreateUserRequest): Promise<ApiResponse<UserWithDetails>> =>
    ApiService.post<UserWithDetails>('/users', userData),

  // Mettre à jour un utilisateur
  updateUser: (userId: string, userData: UpdateUserRequest): Promise<ApiResponse<UserWithDetails>> =>
    ApiService.put<UserWithDetails>(`/users/${userId}`, userData),

  // Supprimer un utilisateur
  deleteUser: (userId: string): Promise<ApiResponse<void>> =>
    ApiService.delete(`/users/${userId}`),

  // Inviter un utilisateur
  inviteUser: (inviteData: UserInviteRequest): Promise<ApiResponse<{ inviteId: string; expiresAt: string }>> =>
    ApiService.post<{ inviteId: string; expiresAt: string }>('/users/invite', inviteData),

  // Activer/Désactiver un utilisateur
  toggleUserStatus: (userId: string): Promise<ApiResponse<UserWithDetails>> =>
    ApiService.patch<UserWithDetails>(`/users/${userId}/toggle-status`, {}),

  // Réinitialiser le mot de passe d'un utilisateur
  resetUserPassword: (userId: string): Promise<ApiResponse<{ resetToken: string; expiresAt: string }>> =>
    ApiService.post<{ resetToken: string; expiresAt: string }>(`/users/${userId}/reset-password`, {}),

  // Obtenir les permissions d'un utilisateur
  getUserPermissions: (userId: string): Promise<ApiResponse<{ permissions: string[] }>> =>
    ApiService.get<{ permissions: string[] }>(`/users/${userId}/permissions`),

  // Mettre à jour les permissions d'un utilisateur
  updateUserPermissions: (userId: string, permissions: string[]): Promise<ApiResponse<{ permissions: string[] }>> =>
    ApiService.put<{ permissions: string[] }>(`/users/${userId}/permissions`, { permissions }),

  // Obtenir l'historique d'activité d'un utilisateur
  getUserActivity: (userId: string, params?: {
    page?: number;
    pageSize?: number;
    from?: string;
    to?: string;
  }): Promise<ApiResponse<{
    activities: Array<{
      id: string;
      action: string;
      resource: string;
      timestamp: string;
      details?: Record<string, any>;
    }>;
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  }>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params?.from) searchParams.append('from', params.from);
    if (params?.to) searchParams.append('to', params.to);
    
    const query = searchParams.toString();
    return ApiService.get(`/users/${userId}/activity${query ? `?${query}` : ''}`);
  }
};
